import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import { PDF_BODY_CLASS, PDF_DESC_CLASS, PDF_SECTION_TITLE_CLASS } from "@/lib/proposalPdfTypography";
import { resolvePdfLineTableColumns } from "@/lib/proposalPdfProductDisplay";
import type { DtoolsStagingLine } from "@shared/dtoolsJsonStaging";

type Props = {
  title: string;
  lines: DtoolsStagingLine[];
  itemHeader?: string;
  testId?: string;
};

/** Compact Item | Description | Qty table for supporting components and services. */
export default function DtoolsPdfCompactLineTable({
  title,
  lines,
  itemHeader = "Item",
  testId,
}: Props) {
  if (lines.length === 0) return null;

  return (
    <section
      className="space-y-1.5"
      data-testid={testId ?? "dtools-pdf-compact-line-table"}
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
    >
      <h3 className={PDF_SECTION_TITLE_CLASS}>{title}</h3>
      {title === "Services" ? (
        <p className={`${PDF_DESC_CLASS} max-w-[640px] leading-snug`}>
          Professional installation, testing, calibration, and client handover are included so the system performs as
          designed from day one.
        </p>
      ) : null}
      <table className="dtools-pdf-compact-table w-full border-collapse text-left" style={{ fontFamily: PROPOSAL_COPY_FONT }}>
        <thead>
          <tr className="border-b border-[#1A2744]/20">
            <th className={`pb-1 pr-3 font-semibold text-[#1A2744] ${PDF_BODY_CLASS}`}>{itemHeader}</th>
            <th className={`pb-1 pr-3 font-semibold text-[#1A2744] ${PDF_BODY_CLASS}`}>Description</th>
            <th className={`pb-1 text-right font-semibold text-[#1A2744] ${PDF_BODY_CLASS}`}>Qty</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const columns = resolvePdfLineTableColumns(line);
            return (
              <tr
                key={line.id}
                className="pdf-keep-together border-b border-[#1A2744]/10"
                data-testid={`dtools-pdf-line-row-${line.id}`}
              >
                <td className={`py-1 pr-3 align-top font-medium text-[#1A2744] ${PDF_BODY_CLASS}`}>{columns.item}</td>
                <td className={`max-w-[320px] py-1 pr-3 align-top ${PDF_DESC_CLASS}`}>{columns.description || "—"}</td>
                <td className={`py-1 text-right align-top tabular-nums text-[#1A2744] ${PDF_BODY_CLASS}`}>
                  {columns.quantity}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
